import { isEqual } from 'lodash';
import { isEmptyObject, noop } from '@splunk/dashboard-utils';
import { DS_STATUS, formatData } from '@splunk/datasource-utils';
import type { Subscription as ObservableSubscription } from 'rxjs';
import type {
    ErrorPayload,
    OnDataCallback,
    OnErrorCallback,
    OnCancelCallback,
    OnStartCallback,
    OnCompleteCallback,
    RequestParams,
    SearchData,
    LastSubscriptionState,
    OnResumeCallback,
    OnPauseCallback,
    OnFinalizeCallback,
    SearchControlAction,
} from '@splunk/dashboard-types';

import type DataSourceController from './DataSourceController';
import { shouldFinalize, shouldPause, shouldResume } from '../utils';

/**
 * A DataSource subscription
 * This represent a DataSource -- Consumer binding.
 * Be aware that the binding can be n to n.
 */
class Subscription {
    consumerId: string;

    dataSource: DataSourceController;

    requestParams?: RequestParams;

    lastState: LastSubscriptionState;

    sub: ObservableSubscription | null;

    cancelCallback?: OnCancelCallback;

    completeCallback?: OnCompleteCallback;

    dataCallback?: OnDataCallback;

    errorCallback?: OnErrorCallback;

    pauseCallback?: OnPauseCallback;

    startCallback?: OnStartCallback;

    resumeCallback?: OnResumeCallback;

    finalizeCallback?: OnFinalizeCallback;

    /**
     *
     * @param {Object} dataSource DataSource controller
     * @param {String} consumerId Consumer id
     * @param {Object} initialRequestParams initial requestParams
     */
    constructor(
        dataSource: DataSourceController,
        consumerId: string,
        initialRequestParams?: RequestParams
    ) {
        this.consumerId = consumerId;
        this.dataSource = dataSource;
        this.requestParams = initialRequestParams;
        this.lastState = {};
        this.sub = null;
    }

    /**
     * get last state of this subscription
     * @returns {Object} last state, either be data or error
     */
    getLastState = (): LastSubscriptionState => {
        return this.lastState;
    };

    /**
     * start polling data
     */
    startPolling(): void {
        this.start();

        if (this.dataSource.setupError) {
            this.error({
                level: 'error',
                message: this.dataSource.setupError,
            });
            return;
        }
        try {
            const ob = this.dataSource.request(this.requestParams);
            this.sub = ob.subscribe(
                ({ data, meta = {} }) => {
                    const formattedData = formatData(data);
                    if (formattedData != null) {
                        const payload = {
                            requestParams: this.requestParams,
                            data: formattedData,
                            meta,
                        };
                        this.lastState = payload;
                        this.progress(payload);
                    } else {
                        this.error({
                            level: 'error',
                            message: `${this.dataSource.def.type} returns invalid data`,
                        });
                    }
                },
                ({ level, message, meta = {} }) => {
                    const payload = {
                        level,
                        message,
                        meta,
                    };
                    this.lastState = payload;
                    this.error(payload);
                },
                () => {
                    this.done();
                }
            );
        } catch (ex) {
            if (ex instanceof Error) {
                this.error({
                    level: 'error',
                    message: ex.message,
                });
            }
        }
    }

    /**
     * cancel this subscription
     */
    cancel(): void {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        this.dataCallback = undefined;
        this.errorCallback = undefined;
        this.lastState = {};
        if (this.cancelCallback) {
            this.cancelCallback(this.consumerId);
        }
    }

    /**
     * refresh current subscription
     */
    refresh(): void {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        this.startPolling();
    }

    /**
     *
     */
    start(): void {
        if (this.startCallback) {
            this.startCallback(this.consumerId);
        }
        if (isEmptyObject(this.lastState)) {
            this.lastState.meta = { status: DS_STATUS.QUEUED };
        }
    }

    private pause(): void {
        this.dataSource.broadcast({
            eventType: 'datasource.pause',
            targetId: this.dataSource.id,
            payload: { consumerId: this.consumerId },
        });
        this.pauseCallback?.(this.consumerId);
    }

    private resume(): void {
        this.dataSource.broadcast({
            eventType: 'datasource.resume',
            targetId: this.dataSource.id,
            payload: { consumerId: this.consumerId },
        });

        this.resumeCallback?.(this.consumerId);
    }

    private finalize(): void {
        this.dataSource.broadcast({
            eventType: 'datasource.finalize',
            targetId: this.dataSource.id,
            payload: { consumerId: this.consumerId },
        });

        this.finalizeCallback?.(this.consumerId);
    }

    control(action: SearchControlAction): void {
        if (shouldFinalize({ status: this.dataSource.getStatus(), action })) {
            this.finalize();
        } else if (
            shouldPause({
                isPaused: this.dataSource.isPaused(),
                action,
            })
        ) {
            this.pause();
        } else if (
            shouldResume({
                isPaused: this.dataSource.isPaused(),
                action,
            })
        ) {
            this.resume();
        }
    }

    progress = (payload: SearchData): void => {
        const currentTime = new Date().getTime();
        if (this.dataCallback) {
            this.dataCallback(payload);
        }

        // once paused or finalizing, the DS should stop sending results_preview requests but we still need to process
        // the ones we receive. Use the corresponding eventType so smart sources don't show the wrong
        // $<search>:job.status$ token value from a 'datasource.progress' broadcast
        let broadcastEvent = 'datasource.progress';
        if (this.dataSource.isPaused()) {
            broadcastEvent = 'datasource.pause';
        } else if (this.dataSource.isFinalizing()) {
            broadcastEvent = 'datasource.finalize';
        }

        this.dataSource.broadcast({
            eventType: broadcastEvent,
            targetId: this.dataSource.id,
            payload: {
                consumerId: this.consumerId,
                currentTime,
                ...payload,
            },
        });
    };

    /**
     * send back error and broadcast error event
     */
    error = (payload: ErrorPayload): void => {
        if (this.errorCallback) {
            this.errorCallback(payload);
        }
        this.dataSource.broadcast({
            eventType: 'datasource.error',
            targetId: this.dataSource.id,
            payload: {
                consumerId: this.consumerId,
                requestParams: this.requestParams,
                error: payload,
            },
        });
        if (this.completeCallback) {
            this.completeCallback(this.consumerId);
        }
    };

    /**
     * broadcast done event
     */
    done = (): void => {
        this.dataSource.broadcast({
            eventType: 'datasource.done',
            targetId: this.dataSource.id,
            payload: {
                consumerId: this.consumerId,
                currentTime: new Date().getTime(),
                // we send back the last state part of done event
                ...this.lastState,
            },
        });
        if (this.completeCallback) {
            this.completeCallback(this.consumerId);
        }
    };

    /**
     * register subscription start callback
     * @param {Function} callback
     */
    onStart(callback: OnStartCallback): void {
        this.startCallback = callback;
    }

    /**
     * register subscription cancel callback
     * @param {Function} callback cancel callback
     */
    onCancel(callback: OnCancelCallback): void {
        this.cancelCallback = callback;
    }

    /**
     * register complete callback
     * @param {Function} callback
     */
    onComplete(callback: OnCompleteCallback): void {
        this.completeCallback = callback;
    }

    /**
     * register subscription pause callback
     * @param {Function} callback
     */
    onPause(callback: OnPauseCallback): void {
        this.pauseCallback = callback;
    }

    /**
     * register subscription resume callback
     * @param {Function} callback
     */
    onResume(callback: OnResumeCallback): void {
        this.resumeCallback = callback;
    }

    /**
     * register subscription finalize callback
     * @param {Function} callback
     */
    onFinalize(callback: OnFinalizeCallback): void {
        this.finalizeCallback = callback;
    }

    /**
     * Subscribe to DataSource data
     * @param {Function} onData data callback
     * @param {Function} onError error callback
     */
    subscribeToData({ onData = noop, onError = noop } = {}): void {
        this.dataCallback = onData;
        this.errorCallback = onError;
        this.startPolling();
    }

    /**
     * update request params for this subscription
     * @param {Object} newRequestParams new request params
     */
    updateRequestParams(newRequestParams: RequestParams): void {
        // only start polling if the request parameters actually changed
        if (isEqual(newRequestParams, this.requestParams)) {
            return;
        }

        if (this.sub) {
            this.sub.unsubscribe();
        }
        this.requestParams = newRequestParams;
        this.startPolling();
    }

    getPauseStatus(): boolean {
        return this.dataSource.isPaused();
    }
}

export default Subscription;
